import {
  EocClientDraftItem,
  EocClientDraftSection,
  EocClientDraftStudyPlan,
  EocClientStudyPlan,
  EocStudyPlanResponse,
  Program,
  StudyPlanScope,
  eocDraftPlanItemModifyResponse,
  eocDraftPlanResponse,
  refScope
} from "../../../types/gqlTypes/graphql"
import { DocumentNode } from "@apollo/client"
import _ from "lodash"

import {
  getCurrentPlanByRefScope,
  getCurrentPlanForEachSubjectByRefScope
} from "./studyPlanQueryResolver"
import {
  removeSectionItem,
  updateSection,
  updateSectionItem
} from "./studyPlanMutationResolver"
import { Transitions } from "../../../modules/eoc/machines/studyPlan/types"

export function generateTargetedRefScope(
  selfRefScope: refScope,
  targetPlanScope?: StudyPlanScope,
  targetPlanRefId?: string
): refScope {
  const scope = targetPlanScope || selfRefScope?.scope
  let district_id = selfRefScope?.district_id
  let school_id = selfRefScope?.school_id
  let teacher_id = selfRefScope?.teacher_id
  let class_id = selfRefScope?.class_id
  let student_id = selfRefScope?.student_id

  // overwrite part
  switch (targetPlanScope) {
    case "STUDENT":
      student_id = targetPlanRefId || selfRefScope.student_id
      break
    case "CLASS":
      class_id = targetPlanRefId || selfRefScope.class_id
      student_id = undefined
      break
    case "TEACHER":
      teacher_id = targetPlanRefId || selfRefScope.teacher_id
      class_id = student_id = undefined
      break
    case "SCHOOL":
      school_id = targetPlanRefId || selfRefScope.school_id
      teacher_id = class_id = student_id = undefined
      break
    case "DISTRICT":
      district_id = targetPlanRefId || selfRefScope.district_id
      school_id = teacher_id = class_id = student_id = undefined
      break
  }

  return {
    scope,
    district_id,
    school_id,
    teacher_id,
    class_id,
    student_id
  }
}

export function generateSelfRefScope(
  // override options are all optional.
  userRole: StudyPlanScope,
  district_id?: string,
  school_id?: string,
  teacher_id?: string,
  class_id?: string,
  student_id?: string
): refScope {
  return {
    scope: userRole,
    district_id,
    school_id,
    teacher_id,
    class_id,
    student_id
  }
}

export function getGqlString(doc: DocumentNode) {
  return doc.loc && doc.loc.source.body
}

export async function getCurrentPlanForEachSubjectByRefScopeBySubject(
  program: Program,
  refScope: refScope
): Promise<{ plans: { [key: string]: EocClientStudyPlan } }> {
  const response = await getCurrentPlanForEachSubjectByRefScope(
    program,
    refScope
  )

  const plans = {}
  response.plans.forEach(plan => {
    if (plan.subject) {
      plans[plan.subject] = plan
    }
  })
  // console.log("plans: " + JSON.stringify({ plans }))
  return { plans }

  // console.log("program: " + program + " refScope: " + JSON.stringify(refScope))
  // console.log("getCurrentPlanForEachSubjectByRefScope response:" + JSON.stringify(response.getCurrentPlanForEachSubjectByRefScope))
}

export async function removeSectionItemByContentId(
  plan: EocClientDraftStudyPlan,
  sectionId: string,
  contentId: string
): Promise<eocDraftPlanItemModifyResponse> {
  const section = plan.sections.find(s => s.id === sectionId)
  if (!section) {
    throw new Error("Unable to find section")
  }
  const itemId = section.items.find(i => i.contentId === contentId).id
  return removeSectionItem(plan.id, sectionId, itemId)
}

export async function toggleSectionBreakBefore(
  planId: string,
  section: EocClientDraftSection
): Promise<eocDraftPlanResponse> {
  const breakBefore = !(section.breakBefore ?? false)
  return updateSection(planId, section.id, {
    breakBefore
  })
}
export async function toggleSectionBreakAfter(
  planId: string,
  section: EocClientDraftSection
): Promise<eocDraftPlanResponse> {
  const breakAfter = !(section.breakAfter ?? false)
  return updateSection(planId, section.id, {
    breakAfter
  })
}

export async function toggleItemBreakBefore(
  planId: string,
  sectionId: string,
  item: EocClientDraftItem
): Promise<eocDraftPlanItemModifyResponse> {
  const breakBefore = !(item.breakBefore ?? false)
  return updateSectionItem(planId, sectionId, item.id, {
    breakBefore
  })
}
export async function toggleItemBreakAfter(
  planId: string,
  sectionId: string,
  item: EocClientDraftItem
): Promise<eocDraftPlanItemModifyResponse> {
  const breakAfter = !(item.breakAfter ?? false)
  return updateSectionItem(planId, sectionId, item.id, {
    breakAfter
  })
}
export async function toggleItemTeachLive(
  planId: string,
  sectionId: string,
  item: EocClientDraftItem
): Promise<eocDraftPlanItemModifyResponse> {
  const teachLive = !(item.teachLive ?? false)
  return updateSectionItem(planId, sectionId, item.id, {
    isTeachLive: teachLive
  })
}

export async function getCurrentPlanWithEventType(
  program: Program,
  subject: string,
  refScope: refScope,
  requirePercentSummary?: boolean,
  event?: Transitions
): Promise<{ response: EocStudyPlanResponse; event: Transitions }> {
  const response = await getCurrentPlanByRefScope(
    program,
    subject,
    refScope,
    requirePercentSummary
  )
  return { response, event }
}
